<template>
  <AppClientSchoolLayout title="学生一覧">
    <div class="max-w-7xl mx-auto p-8">
      <div class="grid grid-cols-12 mb-4">
        <input
          v-model="form.id"
          type="search"
          class="col-span-3 form-input border border-gray-300 outline-none rounded-md py-2 px-4 bg-white placeholder-gray-400 text-gray-500 appearance-none w-full block focus:outline-none"
          placeholder="ユーザID" />
      </div>
      <div class="grid grid-cols-12 mb-4">
        <input
          v-model="form.studentId"
          type="search"
          class="col-span-2 form-input border border-gray-300 outline-none rounded-md py-2 px-4 bg-white placeholder-gray-400 text-gray-500 appearance-none w-full block focus:outline-none"
          placeholder="学籍番号" />
        <select
          v-model="form.serviceYears"
          class="col-span-1 ml-2 border border-gray-300 outline-none rounded-md py-2 px-2 bg-white placeholder-gray-400 text-gray-500 appearance-none w-full block focus:outline-none">
          <option
            v-for="(n, i) in ['', '1', '2', '3', '4']"
            :key="i"
            :value="i">
            {{ n }}
          </option>
        </select>
        <input
          v-model="form.departmentKeyword"
          type="search"
          class="col-span-3 ml-4 form-input border border-gray-300 outline-none rounded-md py-2 px-4 bg-white placeholder-gray-400 text-gray-500 appearance-none w-full block focus:outline-none"
          placeholder="学部・学科" />
        <input
          v-model="form.nameKeyword"
          type="search"
          class="col-span-3 ml-6 form-input border border-gray-300 outline-none rounded-md py-2 px-4 bg-white placeholder-gray-400 text-gray-500 appearance-none w-full block focus:outline-none"
          placeholder="名前" />
        <button
          class="col-span-1 relative ml-8 px-6 py-2 rounded bg-blue-600 text-blue-50 max-w-max shadow-sm hover:shadow-lg"
          @click.prevent="onSearchButtonClicked">
          <svg
            class="absolute pointer-events-none w-6 h-6 top-1/4 left-1/4 text-white"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24">
            <path
              d="M16.32 14.9l5.39 5.4a1 1 0 0 1-1.42 1.4l-5.38-5.38a8 8 0 1 1 1.41-1.41zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z" />
          </svg>
        </button>
        <button
          class="col-span-2 bg-white hover:bg-gray-100 text-black ml-4 py-2 px-1 border border-gray-200 rounded shadow"
          @click.prevent="onResetButtonClicked">
          リセット
        </button>
      </div>
      <div class="shadow-lg bg-white mr-1 rounded-b-lg rounded-t">
        <div v-if="users.data.length">
          <data-table
            hover-disabled
            drag-disabled
            :sort="sort"
            :order="order"
            :headers="headers"
            :items="users.data"
            @row-click="onRowClicked"
            @header-click="onClickHeader">
            <template #id="{ item }">
              <div class="text-left px-6">
                {{ item.id ?? "-" }}
              </div>
            </template>
            <template #student_id="{ item }">
              <div class="text-left px-6">
                {{ item.student_id ?? "-" }}
              </div>
            </template>
            <template #service_years="{ item }">
              <div class="text-left px-6">
                {{ convertToGrade(item.service_years) }}
                <!-- {{ calcGrade(item.date_of_admission) }} -->
              </div>
            </template>
            <template #department="{ item }">
              <div
                class="text-left px-6"
                style="width: 200px">
                {{ item.department ?? "-" }}
              </div>
            </template>
            <template #name="{ item }">
              <div
                class="text-left px-6"
                style="width: 200px">
                {{ item.name ?? "-" }}
              </div>
            </template>
            <template #cancellation>
              <div class="font-bold text-center px-6 cursor-pointer">
                連携解除
              </div>
            </template>
          </data-table>
          <div
            v-if="users.total > paginatePerPage"
            class="mb-5 pb-5">
            <div class="mt-4">
              <pagination
                :paginate="users"
                @page-previous="onPaginationChanged"
                @page-next="onPaginationChanged"
                @page-click="onPaginationChanged" />
            </div>
          </div>
        </div>
        <div v-else>
          <div
            class="mx-auto w-11/12 bg-red-100 border border-red-400 text-red-500 px-4 py-3 rounded relative text-center"
            role="alert">
            <span
              class="block sm:inline font-bold">ユーザーが見つかりませんでした。</span>
          </div>
        </div>
      </div>
    </div>
  </AppClientSchoolLayout>
</template>

<script setup>
import { Inertia } from '@inertiajs/inertia'
import {
  reactive, ref, onMounted, computed,
} from 'vue'
import moment from 'moment'
import Swal from 'sweetalert2'
import convertToGrade from '@/actions/serviceYears/convert'
import AppClientSchoolLayout from '@/Layouts/AppClientSchoolLayout'
import DataTable from '@/Common/DataTable'
import Pagination from '../../../Common/Pagination'
import DialogModal from '../../../Common/DialogModal'

const props = defineProps({
  users: {
    type: Object,
    default: () => ({}),
  },
  limit: {
    type: String,
    default: '15',
  },
  sort: {
    type: String,
    default: 'id',
  },
  order: {
    type: String,
    default: 'asc',
  },
  id: {
    type: String,
    default: null,
  },
  studentId: {
    type: String,
    default: null,
  },
  serviceYears: {
    type: String,
    default: null,
  },
  departmentKeyword: {
    type: String,
    default: null,
  },
  nameKeyword: {
    type: String,
    default: null,
  },
})

const showModal = ref(false)
const paginatePerPage = ref('15')

const headers = {
  id: 'ユーザID',
  student_id: '学籍番号',
  service_years: '学年',
  department: '学部・学科',
  name: '名前',
  cancellation: ' ',
}

const form = reactive(
  Inertia.form({
    id: props.id,
    studentId: props.studentId,
    serviceYears: props.serviceYears,
    departmentKeyword: props.departmentKeyword,
    nameKeyword: props.nameKeyword,
  }),
)

// const calcGrade = (admissionDate) => {
//   if(admissionDate == null){
//     return '-'
//   }
//   const today = new Date();
//   const formatAdmissionDate = new Date(admissionDate);
//   let maxDate=new Date(3-1,31)
//   let minDate=new Date(1-1,1)
//   let serviceYears = moment(today).diff(admissionDate,'year')+1
//
//   if(maxDate >= today && minDate <= today){
//     serviceYears=serviceYears-1
//   }
//   return serviceYears
// }

const onSearchButtonClicked = () => {
  Inertia.get(
    route('school.students', {
      sort: props.sort,
      order: props.order,
      id: form.id,
      studentId: form.studentId,
      serviceYears: form.serviceYears,
      departmentKeyword: form.departmentKeyword,
      nameKeyword: form.nameKeyword,
      limit: props.limit,
    }),
  )
}

const onResetButtonClicked = () => {
  form.id = ''
  form.studentId = ''
  form.serviceYears = ''
  form.departmentKeyword = ''
  form.nameKeyword = ''
  Inertia.get(
    route('school.students', {
      sort: props.sort,
      order: props.order,
      id: form.id,
      studentId: form.studentId,
      serviceYears: form.serviceYears,
      departmentKeyword: form.departmentKeyword,
      nameKeyword: form.nameKeyword,
      limit: props.limit,
    }),
  )
}

const onClickHeader = (key) => {
  if (key === 'cancellation') {
    return
  }
  let { order } = props
  if (props.sort === key) {
    order = order === 'asc' ? 'desc' : 'asc'
  } else {
    order = 'asc'
  }
  Inertia.get(
    route('school.students', {
      sort: key,
      order,
      limit: props.limit,
      id: props.id,
      studentId: props.studentId,
      serviceYears: props.serviceYears,
      departmentKeyword: props.departmentKeyword,
      nameKeyword: props.nameKeyword,
    }),
  )
}

const onPaginationChanged = (page) => {
  Inertia.get(
    route('school.students', {
      page,
      limit: props.limit,
      sort: props.sort,
      order: props.order,
      id: props.id,
      studentId: props.studentId,
      serviceYears: props.serviceYears,
      departmentKeyword: props.departmentKeyword,
      nameKeyword: props.nameKeyword,
    }),
  )
}

const onRowClicked = (item, title) => {
  if (title === ' ') {
    Swal.fire({
      icon: 'question',
      html:
        `このユーザーの学校連携を解除しますか？<br><br>学籍番号：${
          item.student_id
        }<br>名前：${
          item.name}`,
      showCancelButton: true,
      confirmButtonText: 'はい',
      cancelButtonText: 'いいえ',
      confirmButtonColor: '#FF8A80',
    }).then((result) => {
      if (result.isConfirmed) {
        Inertia.visit(route('school.students.unlink'), {
          method: 'post',
          data: {
            userId: item.id,
          },
        })
      }
    })
  }
}
</script>

<style></style>
